import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  MessageSquare, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  RefreshCw, Loader2, User, Clock, CheckCircle, Send, ExternalLink,
  ChevronRight, Mic, Search, X, PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatPhone } from '../../utils/formatPhone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(secs) {
  if (!secs || secs <= 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTime(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'MMM d, h:mm a'); } catch { return ''; }
}

// ── Status badge with semantic colours ───────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    completed: 'bg-green-50 text-green-700 border-green-200',
    answered:  'bg-blue-50  text-blue-700  border-blue-200',
    missed:    'bg-red-50   text-red-700   border-red-200',
    failed:    'bg-red-50   text-red-700   border-red-200',
    ringing:   'bg-yellow-50 text-yellow-700 border-yellow-200',
    sent:      'bg-green-50 text-green-700 border-green-200',
    received:  'bg-slate-50 text-slate-600 border-slate-200',
    delivered: 'bg-green-50 text-green-700 border-green-200',
  };
  const cls = map[(status || '').toLowerCase()] || 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium capitalize ${cls}`}>
      {status || 'unknown'}
    </span>
  );
}

// ── Call Detail Modal ─────────────────────────────────────────────────────────

function CallDetailModal({ call, isAdmin, onClose }) {
  if (!call) return null;
  const isInbound = call.direction === 'inbound';
  const isMissed  = call.status === 'missed';
  const contactNumber = isInbound ? call.from_number : call.to_number;
  const contactName   = call.lead_name || formatPhone(contactNumber);

  return (
    <Dialog open={!!call} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {isMissed ? (
              <PhoneMissed className="h-5 w-5 text-red-500" />
            ) : isInbound ? (
              <PhoneIncoming className="h-5 w-5 text-green-600" />
            ) : (
              <PhoneOutgoing className="h-5 w-5 text-blue-600" />
            )}
            {isInbound ? 'Inbound' : 'Outbound'} Call
            <StatusBadge status={call.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Contact card */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm font-semibold text-slate-900">{contactName}</p>
              <p className="text-xs text-slate-500 mt-0.5">{formatPhone(contactNumber)}</p>
            </div>
            {call.lead_id && (
              <Link to={`/admin/leads/${call.lead_id}`} onClick={onClose}>
                <Button variant="outline" size="sm" className="text-xs h-7">
                  <User className="h-3 w-3 mr-1" />
                  View Lead
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            )}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">From</p>
              <p className="font-medium text-slate-800">{formatPhone(call.from_number) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">To</p>
              <p className="font-medium text-slate-800">{formatPhone(call.to_number) || '—'}</p>
            </div>

            {isAdmin && call.user_name && (
              <div className="col-span-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Handled By</p>
                <p className="font-medium text-slate-800">{call.user_name}</p>
              </div>
            )}

            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Date & Time</p>
              <p className="font-medium text-slate-800">{formatTime(call.created_at) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Duration</p>
              <p className="font-medium text-slate-800">{formatDuration(call.duration_secs) || '—'}</p>
            </div>

            {call.answered_at && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Answered</p>
                <p className="font-medium text-slate-800">{formatTime(call.answered_at)}</p>
              </div>
            )}
            {call.ended_at && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Ended</p>
                <p className="font-medium text-slate-800">{formatTime(call.ended_at)}</p>
              </div>
            )}
          </div>

          {/* Recording player */}
          {call.playback_url && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Recording</p>
              <audio
                controls
                src={`${process.env.REACT_APP_BACKEND_URL}${call.playback_url}`}
                className="w-full"
                style={{ height: '36px' }}
              />
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── SMS Detail / Reply Modal ──────────────────────────────────────────────────

function SMSDetailModal({ sms, isAdmin, onClose, onReply, onMarkRead }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending]     = useState(false);

  useEffect(() => { setReplyText(''); }, [sms]);

  if (!sms) return null;
  const isInbound   = sms.direction !== 'outbound';
  const contactName = sms.lead_name || formatPhone(isInbound ? sms.from_number : sms.to_number);

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await onReply(sms, replyText);
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!sms} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            {isInbound ? 'Inbound' : 'Outbound'} SMS
            <StatusBadge status={sms.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Contact card */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm font-semibold text-slate-900">{contactName}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {formatPhone(isInbound ? sms.from_number : sms.to_number)}
              </p>
            </div>
            {sms.lead_id && (
              <Link to={`/admin/leads/${sms.lead_id}`} onClick={onClose}>
                <Button variant="outline" size="sm" className="text-xs h-7">
                  <User className="h-3 w-3 mr-1" />
                  View Lead
                </Button>
              </Link>
            )}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">From</p>
              <p className="font-medium text-slate-800">{formatPhone(sms.from_number) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">To</p>
              <p className="font-medium text-slate-800">{formatPhone(sms.to_number) || '—'}</p>
            </div>

            {isAdmin && sms.user_name && (
              <div className="col-span-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Handled By</p>
                <p className="font-medium text-slate-800">{sms.user_name}</p>
              </div>
            )}

            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Date & Time</p>
              <p className="font-medium text-slate-800">{formatTime(sms.created_at) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Direction</p>
              <p className="font-medium text-slate-800 capitalize">{sms.direction || '—'}</p>
            </div>
          </div>

          {/* Message body */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-1.5">Message</p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{sms.message}</p>
          </div>

          {/* Reply textarea — inbound only */}
          {isInbound && (
            <div className="space-y-1.5">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Reply</p>
              <Textarea
                placeholder="Type your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {isInbound && (
            <Button onClick={handleSend} disabled={sending || !replyText.trim()}>
              {sending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Send className="h-4 w-4 mr-2" />}
              Send Reply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Stats card (admin only) ───────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  const colors = {
    default: 'border-slate-200 text-slate-900',
    red:     'border-red-100   text-red-600',
    blue:    'border-blue-100  text-blue-600',
  };
  return (
    <div className={`bg-white border rounded-lg px-4 py-3 ${colors[color] || colors.default}`}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-0.5 ${colors[color]?.split(' ')[1] || 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

// ── Main InboxPage ────────────────────────────────────────────────────────────

export default function InboxPage() {
  const { isAdmin } = useAuth();

  const [activeTab,     setActiveTab]     = useState('sms');
  const [smsMessages,   setSmsMessages]   = useState([]);
  const [calls,         setCalls]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [selectedCall,  setSelectedCall]  = useState(null);
  const [selectedSms,   setSelectedSms]   = useState(null);

  // Persist "last viewed calls tab" timestamp in sessionStorage so it survives navigation
  const [callsLastViewedAt, setCallsLastViewedAt] = useState(
    () => sessionStorage.getItem('inbox_calls_viewed_at') || null
  );
  const activeTabRef = useRef(activeTab);
  const smsRef       = useRef([]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { smsRef.current = smsMessages; },    [smsMessages]);

  // Filters
  const [searchQuery,     setSearchQuery]     = useState('');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [statusFilter,    setStatusFilter]    = useState('all');
  const [dateFilter,      setDateFilter]      = useState('');

  // Mark ALL inbound SMS as read in one backend call, update local state
  const markAllSmsRead = useCallback(() => {
    const hasUnread = smsRef.current.some(m => !m.read && m.direction !== 'outbound');
    if (!hasUnread) return;
    setSmsMessages(prev => prev.map(m => ({ ...m, read: true })));
    setUnreadCount(0);
    axios.put(`${API}/telnyx/sms/mark-all-read`).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [smsRes, callsRes] = await Promise.all([
        axios.get(`${API}/telnyx/sms/inbound`, { params: { include_outbound: true, limit: 0 } }),
        axios.get(`${API}/telnyx/calls`,        { params: { limit: 0 } }),
      ]);
      const messages = smsRes.data.messages || [];
      const fetchedCalls = callsRes.data.calls || [];

      // If already on SMS tab when data arrives, auto-mark all as read
      if (activeTabRef.current === 'sms') {
        setSmsMessages(messages.map(m => ({ ...m, read: true })));
        setUnreadCount(0);
        if (messages.some(m => !m.read && m.direction !== 'outbound')) {
          axios.put(`${API}/telnyx/sms/mark-all-read`).catch(() => {});
        }
      } else {
        setSmsMessages(messages);
        setUnreadCount(smsRes.data.unread_count || 0);
      }

      setCalls(fetchedCalls);
    } catch (err) {
      console.error('Failed to fetch inbox:', err);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // When switching tabs, mark that tab's items as "seen"
  useEffect(() => {
    if (activeTab === 'sms') {
      markAllSmsRead();
    } else if (activeTab === 'calls') {
      const now = new Date().toISOString();
      setCallsLastViewedAt(now);
      sessionStorage.setItem('inbox_calls_viewed_at', now);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkRead = async (smsId) => {
    try {
      await axios.put(`${API}/telnyx/sms/${smsId}/read`);
      setSmsMessages(prev => prev.map(m => m.id === smsId ? { ...m, read: true } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark-read failed:', err);
    }
  };

  const handleReply = async (sms, text) => {
    try {
      await axios.post(`${API}/telnyx/sms`, {
        to_number: sms.from_number,
        message:   text,
        lead_id:   sms.lead_id,
      });
      toast.success('Reply sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
      throw err;
    }
  };

  // ── Filter helpers ────────────────────────────────────────────────────────

  const applyFilters = (items) => items.filter(item => {
    const name  = (item.lead_name || '').toLowerCase();
    const phone = `${item.from_number || ''} ${item.to_number || ''}`.toLowerCase();
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!name.includes(q) && !phone.includes(q)) return false;
    }
    if (directionFilter !== 'all' && item.direction !== directionFilter) return false;
    if (statusFilter    !== 'all' && item.status    !== statusFilter)    return false;
    if (dateFilter) {
      if (!item.created_at) return false;
      try {
        if (format(parseISO(item.created_at), 'yyyy-MM-dd') !== dateFilter) return false;
      } catch {
        return false;
      }
    }
    return true;
  });

  const filteredSms   = applyFilters(smsMessages);
  const filteredCalls = applyFilters(calls);

  const missedCalls = calls.filter(c => c.status === 'missed').length;
  // Unseen = missed calls that arrived AFTER the last time the user viewed the Calls tab
  // callsLastViewedAt is null when the tab has never been viewed → all missed calls are unseen
  const unseenMissedCalls = calls.filter(c =>
    c.status === 'missed' &&
    (!callsLastViewedAt || c.created_at > callsLastViewedAt)
  ).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-5" data-testid="inbox-page">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isAdmin ? 'Communications Inbox' : 'Inbox'}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {isAdmin
              ? 'Full call and SMS activity across all team members'
              : 'Your call history and SMS messages'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ── Admin stats row ── */}
      {isAdmin && !loading && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total Calls"  value={calls.length}  color="default" />
          <StatCard label="Missed Calls" value={missedCalls}   color="red"     />
          <StatCard label="Unread SMS"   value={unreadCount}   color="blue"    />
        </div>
      )}

      {/* ── Tabs + Filters ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>

        <div className="flex items-center justify-between flex-wrap gap-3">

          {/* Tab triggers */}
          <TabsList>
            <TabsTrigger value="sms" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              SMS
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="calls" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Calls
              {unseenMissedCalls > 0 && (
                <Badge className="ml-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs bg-red-500 hover:bg-red-500">
                  {unseenMissedCalls}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Filter bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Search name or number…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 w-52 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="h-8 w-36 text-sm">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Directions</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-36 text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-8 w-40 text-sm"
              title="Filter by date"
              data-testid="inbox-date-filter"
            />

            {(searchQuery || directionFilter !== 'all' || statusFilter !== 'all' || dateFilter) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-slate-500"
                onClick={() => { setSearchQuery(''); setDirectionFilter('all'); setStatusFilter('all'); setDateFilter(''); }}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* ════════════════════════════════════════════════ SMS Tab */}
        <TabsContent value="sms" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : filteredSms.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No SMS messages found</p>
              {(searchQuery || directionFilter !== 'all' || statusFilter !== 'all' || dateFilter) && (
                <p className="text-sm text-slate-400 mt-1">Try clearing your filters</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSms.map((msg) => {
                const isInbound   = msg.direction !== 'outbound';
                const unread      = isInbound && !msg.read;
                const contactName = msg.lead_name
                  || formatPhone(isInbound ? msg.from_number : msg.to_number);

                return (
                  <div
                    key={msg.id}
                    onClick={() => {
                      setSelectedSms(msg);
                      if (unread) handleMarkRead(msg.id);
                    }}
                    className={[
                      'p-4 rounded-lg border cursor-pointer transition-all',
                      'hover:shadow-sm hover:border-slate-300',
                      unread ? 'bg-blue-50/60 border-blue-200' : 'bg-white border-slate-200',
                    ].join(' ')}
                    data-testid={`sms-${msg.id}`}
                  >
                    <div className="flex items-start gap-3">

                      {/* Icon */}
                      <div className={[
                        'p-2 rounded-full shrink-0 mt-0.5',
                        isInbound
                          ? unread ? 'bg-blue-100' : 'bg-slate-100'
                          : 'bg-blue-50',
                      ].join(' ')}>
                        <MessageSquare className={[
                          'h-4 w-4',
                          isInbound
                            ? unread ? 'text-blue-600' : 'text-slate-400'
                            : 'text-blue-400',
                        ].join(' ')} />
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        {/* Top row: name + badges */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-medium text-sm ${unread ? 'text-slate-900' : 'text-slate-700'}`}>
                            {contactName}
                          </span>
                          {unread && (
                            <Badge className="bg-blue-600 text-xs h-4 px-1.5 font-normal">New</Badge>
                          )}
                          {!isInbound && (
                            <Badge variant="outline" className="text-xs h-4 px-1.5 text-blue-600 border-blue-200">
                              Sent
                            </Badge>
                          )}
                          <StatusBadge status={msg.status} />
                          {msg.lead_id && (
                            <Link
                              to={`/admin/leads/${msg.lead_id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Badge variant="outline" className="text-xs h-4 px-1.5 cursor-pointer hover:bg-slate-50 text-slate-500">
                                <User className="h-2.5 w-2.5 mr-1" />Lead
                              </Badge>
                            </Link>
                          )}
                        </div>

                        {/* Message preview */}
                        <p className={[
                          'text-sm mt-1 truncate',
                          unread ? 'text-slate-700 font-medium' : 'text-slate-500',
                        ].join(' ')}>
                          {msg.message}
                        </p>

                        {/* Meta row */}
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(msg.created_at)}
                          </span>
                          {isInbound
                            ? <span>From: {formatPhone(msg.from_number)}</span>
                            : <span>To: {formatPhone(msg.to_number)}</span>
                          }
                          {isAdmin && msg.user_name && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <User className="h-3 w-3" />
                              {msg.user_name}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right actions */}
                      <div className="flex items-center gap-1 shrink-0 self-center">
                        {unread && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 rounded-full"
                            title="Mark as read"
                            onClick={(e) => { e.stopPropagation(); handleMarkRead(msg.id); }}
                          >
                            <CheckCircle className="h-4 w-4 text-slate-400 hover:text-green-500" />
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ════════════════════════════════════════════════ Calls Tab */}
        <TabsContent value="calls" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-16">
              <Phone className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No call records found</p>
              {(searchQuery || directionFilter !== 'all' || statusFilter !== 'all' || dateFilter) && (
                <p className="text-sm text-slate-400 mt-1">Try clearing your filters</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCalls.map((call) => {
                const isInbound = call.direction === 'inbound';
                const isMissed  = call.status === 'missed';
                const contactNumber = isInbound ? call.from_number : call.to_number;
                const contactName   = call.lead_name || formatPhone(contactNumber);
                const dur           = formatDuration(call.duration_secs);

                return (
                  <div
                    key={call.id}
                    onClick={() => setSelectedCall(call)}
                    className={[
                      'p-4 rounded-lg border cursor-pointer transition-all',
                      'hover:shadow-sm hover:border-slate-300',
                      isMissed
                        ? 'bg-red-50/40 border-red-100'
                        : 'bg-white border-slate-200',
                    ].join(' ')}
                    data-testid={`call-${call.id}`}
                  >
                    <div className="flex items-center gap-3">

                      {/* Direction icon */}
                      <div className={[
                        'p-2 rounded-full shrink-0',
                        isMissed  ? 'bg-red-100'   :
                        isInbound ? 'bg-green-100'  : 'bg-blue-100',
                      ].join(' ')}>
                        {isMissed ? (
                          <PhoneMissed className="h-4 w-4 text-red-500" />
                        ) : isInbound ? (
                          <PhoneIncoming className="h-4 w-4 text-green-600" />
                        ) : (
                          <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Top row */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-medium text-sm ${isMissed ? 'text-red-700' : 'text-slate-900'}`}>
                            {contactName}
                          </span>
                          <StatusBadge status={call.status} />
                          {call.lead_id && (
                            <Link
                              to={`/admin/leads/${call.lead_id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Badge variant="outline" className="text-xs h-4 px-1.5 cursor-pointer hover:bg-slate-50 text-slate-500">
                                <User className="h-2.5 w-2.5 mr-1" />Lead
                              </Badge>
                            </Link>
                          )}
                          {call.playback_url && (
                            <Badge variant="outline" className="text-xs h-4 px-1.5 text-purple-600 border-purple-200">
                              <Mic className="h-2.5 w-2.5 mr-1" />Recorded
                            </Badge>
                          )}
                        </div>

                        {/* Meta row */}
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(call.created_at)}
                          </span>
                          <span>
                            {formatPhone(call.from_number)} → {formatPhone(call.to_number)}
                          </span>
                          {dur && (
                            <span className="flex items-center gap-1">
                              <PhoneCall className="h-3 w-3" />
                              {dur}
                            </span>
                          )}
                          {isAdmin && call.user_name && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <User className="h-3 w-3" />
                              {call.user_name}
                            </span>
                          )}
                        </div>
                      </div>

                      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

      </Tabs>

      {/* ── Modals ── */}
      <CallDetailModal
        call={selectedCall}
        isAdmin={isAdmin}
        onClose={() => setSelectedCall(null)}
      />
      <SMSDetailModal
        sms={selectedSms}
        isAdmin={isAdmin}
        onClose={() => setSelectedSms(null)}
        onReply={handleReply}
        onMarkRead={handleMarkRead}
      />
    </div>
  );
}
